import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Clock, RotateCw } from "lucide-react";
import { api } from "@/lib/api";

const day = (iso) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

export const useSentReports = (enabled) => useQuery({ queryKey: ["sent-reports"], queryFn: async () => (await api.get("/reports/sent")).data, enabled });

export function SentReports({ open, onResend, busy }) {
  const { data: list = [], isLoading } = useSentReports(open);
  return (
    <div data-testid="sent-reports">
      <p className="tf-tag mb-2">Sent reports</p>
      {isLoading ? <p className="text-sm text-t-muted">Loading…</p> : !list.length ? (
        <p className="text-sm text-t-muted" data-testid="sent-reports-empty">Reports you send to your psychologist will appear here.</p>
      ) : (
        <ul className="flex flex-col gap-2 max-h-56 overflow-y-auto pr-1">
          {list.map((r, i) => {
            const active = new Date(r.expiresAt) > new Date();
            return (
              <li key={r.id} className="rounded-2xl bg-t-surface2 border border-t-border px-4 py-3" data-testid={`sent-report-${i}`}>
                <div className="flex items-start justify-between gap-3">
                  <p className="font-semibold text-t-on text-sm">{r.title}{r.period ? <span className="font-normal text-t-muted"> · {r.period}</span> : null}</p>
                  <span className={`shrink-0 inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full ${active ? "bg-t-brand2 text-t-onbrand2" : "bg-t-surface3 text-t-muted"}`} data-testid={`sent-report-status-${i}`}>
                    {active ? <CheckCircle2 size={12} /> : <Clock size={12} />}{active ? `Active until ${day(r.expiresAt)}` : "Link expired"}
                  </span>
                </div>
                {!active ? (
                  <button onClick={() => onResend(r)} disabled={busy} data-testid={`sent-report-resend-${i}`}
                    className="mt-2 inline-flex items-center gap-1.5 text-[13px] font-semibold text-t-brand hover:opacity-80 transition-opacity disabled:opacity-40"><RotateCw size={13} /> Send again</button>
                ) : null}
                {r.notes?.length ? (
                  <div className="mt-2 flex flex-col gap-1.5" data-testid={`sent-report-notes-${i}`}>
                    {r.notes.map((n) => (
                      <p key={n.id} className="text-sm text-t-on rounded-xl bg-t-brand3 px-3 py-2 whitespace-pre-line">
                        {!n.read ? <span className="mr-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-t-brand text-t-onbrand align-middle">New</span> : null}{n.text}
                      </p>
                    ))}
                  </div>
                ) : null}
                <p className="mt-1 text-xs text-t-on2">To {r.therapistName ? `${r.therapistName} · ` : ""}{r.to} · sent {day(r.sentAt)}, {new Date(r.sentAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</p>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
